import {
  Box,
  Card,
  Hidden,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Clipboard as ClipboardIcon, Edit as EditIcon, Eye as EyeIcon } from 'react-feather';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Link as RouterLink } from 'react-router-dom';
import EmptyList from 'src/components/EmptyList';

const useStyles = makeStyles((theme) => ({
  root: {},
  table_list: {
    '& th': {
      padding: '5px',
    },
    '& td': {
      padding: '5px',
      color: theme.palette.tertiary.main,
      '& a,button': {
        padding: '0px 5px',
      },
    },
  },
}));

const Results = ({
  className,
  patients,
  page,
  setPage,
  resetPage,
  pageSize,
  setPageSize,
  sort,
  setSort,
  isLoading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('patients');
  const { t: tCommon } = useTranslation('common');

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleSortChange = (order) => {
    resetPage();

    if (sort === order) {
      setSort(`-${order}`);
    } else {
      setSort(order);
    }
  };

  return (
    <Card className={clsx(classes.root, className)}>
      <PerfectScrollbar>
        <Box>
          <Table className={classes.table_list}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sort === 'firstName' || sort === '-firstName'}
                    direction={sort === 'firstName' ? 'asc' : 'desc'}
                    onClick={() => {
                      handleSortChange('firstName');
                    }}
                  >
                    {t('fields.firstName')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sort === 'lastName' || sort === '-lastName'}
                    direction={sort === 'lastName' ? 'asc' : 'desc'}
                    onClick={() => {
                      handleSortChange('lastName');
                    }}
                  >
                    {t('fields.lastName')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sort === 'cardNumber' || sort === '-cardNumber'}
                    direction={sort === 'cardNumber' ? 'asc' : 'desc'}
                    onClick={() => {
                      handleSortChange('cardNumber');
                    }}
                  >
                    {t('fields.cardNumber')}
                  </TableSortLabel>
                </TableCell>
                <Hidden smDown>
                  <TableCell>
                    <TableSortLabel
                      active={sort === 'email' || sort === '-email'}
                      direction={sort === 'email' ? 'asc' : 'desc'}
                      onClick={() => {
                        handleSortChange('email');
                      }}
                    >
                      {t('fields.dni_nie')}
                    </TableSortLabel>
                  </TableCell>
                </Hidden>
                <Hidden xsDown>
                  <TableCell>
                    <TableSortLabel
                      active={sort === 'type' || sort === '-type'}
                      direction={sort === 'type' ? 'asc' : 'desc'}
                      onClick={() => {
                        handleSortChange('type');
                      }}
                    >
                      {t('fields.type')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sort === 'companyId' || sort === '-companyId'}
                      direction={sort === 'companyId' ? 'asc' : 'desc'}
                      onClick={() => {
                        handleSortChange('companyId');
                      }}
                    >
                      {t('fields.company')}
                    </TableSortLabel>
                  </TableCell>
                </Hidden>
                <TableCell align="right">{tCommon('tables.rows.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients?.rows.map((patient) => (
                <TableRow hover key={patient.id}>
                  <TableCell>{patient.firstName}</TableCell>
                  <TableCell>{patient.lastName}</TableCell>
                  <TableCell>{patient.cardNumber}</TableCell>
                  <Hidden smDown>
                    <TableCell>{patient.email}</TableCell>
                  </Hidden>
                  <Hidden xsDown>
                    <TableCell>{patient.type}</TableCell>
                    <TableCell>{patient.company?.name || patient.companyId}</TableCell>
                  </Hidden>

                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      value={patient.id}
                      component={RouterLink}
                      to={`/app/patients/${patient.id}/edit`}
                      disabled={isLoading}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      value={patient.id}
                      component={RouterLink}
                      to={`/app/patients/${patient.id}`}
                      disabled={isLoading}
                    >
                      <EyeIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!isLoading && !patients?.rows.length && (
            <EmptyList text={t('labels.empty_list_text')}>
              <ClipboardIcon />
            </EmptyList>
          )}
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={patients?.count || 0}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePageSizeChange}
        page={patients?.count ? page - 1 : 0}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Card>
  );
};

Results.propTypes = {
  patients: PropTypes.object,
  page: PropTypes.number,
  setPage: PropTypes.func,
  resetPage: PropTypes.func,
  pageSize: PropTypes.number,
  setPageSize: PropTypes.func,
  sort: PropTypes.string,
  setSort: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default Results;
